// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import type { RouterHistory } from 'react-router';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import BomsTable from './BomsTable';
import type { PaginatedBomsContainer_boms as BomsFragment } from './__generated__/PaginatedBomsContainer_boms';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  boms: BomsFragment,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedBomsContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200) && relay.hasMore()) {
      relay.loadMore(30);
    }
  };

  render() {
    const { history, boms, relay, filtersDirty } = this.props;

    return (
      <>
        <BomsTable
          history={history}
          filtersDirty={filtersDirty}
          itemEdges={boms.items.edges}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedBomsContainer,
  {
    boms: graphql`
      fragment PaginatedBomsContainer_boms on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "ItemSort" }
        ) {
        items(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          hasBom: true
        ) @connection(key: "PaginatedBomsContainer_items", filters: []) {
          edges {
            cursor
            ...BomsTable_itemEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedBomsContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: ItemSort
      ) {
        ...PaginatedBomsContainer_boms
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
